import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Color, TableAppearance } from '@lsq-frontend/common-ui';
import {
    FeatureFlagService,
    getNavigationItemsForAtlas,
    getRouterLinkUrlById,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    atlas,
    supplyChainDirectBuyerInsightsAtlas,
    SUPPLY_CHAIN_DIRECT_BUYER_INSIGHTS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, pluck, switchMap } from 'rxjs/operators';
import { EMPTY, merge, ReplaySubject } from 'rxjs';
import { BuyerInsightsService } from './insights.service';

@Component({
    selector: 'supply-chain-direct-buyer-insights',
    template: `
        <div class="supply-chain-direct-page min-full-height-content">
            <page-header i18n-title titleText="Insights"> </page-header>

            <div class="container-fluid page-container">
                <div class="insights-inner">
                    <div class="insights-navigation" role="navigation">
                        <ul class="list-unstyled">
                            <ng-container *ngFor="let navigationItem of navigationItems">
                                <ng-container *featureFlag="navigationItem.featureFlags!">
                                    <ng-container *environments="navigationItem.environments!">
                                        <ng-container
                                            *ngIf="
                                                navigationItem.label != 'Working Capital Lift' ||
                                                (navigationItem.label == 'Working Capital Lift' &&
                                                    (workingCapitalLiftIsOn$ | async))
                                            "
                                        >
                                            <li *claimsOr="navigationItem.claims!">
                                                <a
                                                    #routerLinkActive="routerLinkActive"
                                                    routerLinkActive="active"
                                                    [routerLink]="navigationItem.routerLink"
                                                >
                                                    {{ navigationItem.label }}
                                                    <span
                                                        *ngIf="routerLinkActive.isActive"
                                                        class="sr-only"
                                                    >
                                                        (you are here)
                                                    </span>
                                                </a>
                                            </li>
                                        </ng-container>
                                    </ng-container>
                                </ng-container>
                            </ng-container>
                        </ul>
                    </div>

                    <div class="insights-router-container">
                        <card class="insights-card">
                            <div card-header>
                                <h3 id="insights-card-heading">
                                    {{ reportTitle$ | async }}
                                </h3>
                            </div>
                            <div card-body [style.padding-top]="'20px'">
                                <router-outlet></router-outlet>
                            </div>
                        </card>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./insights.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BuyerInsightsComponent implements OnInit {
    public buyerAnalyticsFeatureIsOn$ = this._featureFlagService.isOn(
        FeatureFlags.BuyerExperienceAnalyticsInsights
    );
    public workingCapitalLiftIsOn$ = this._insightsService.workingCapitalLiftIsActivatedForBuyer();
    public readonly tableAppearance = TableAppearance;
    public readonly color = Color;
    public readonly featureFlags = FeatureFlags;
    public readonly insightsTitleI18n = this._i18n('Insights');
    public readonly navigationItems = getNavigationItemsForAtlas(
        supplyChainDirectBuyerInsightsAtlas,
        getRouterLinkUrlById(atlas, SUPPLY_CHAIN_DIRECT_BUYER_INSIGHTS)
    );
    // The Router.events stream fires when we navigate while on the Insights page.
    // It does not fire an event, however, when we navigate to Insights from elsewhere.
    // We therefore need a special ReplaySubject that fires just when we load the Insights page.
    private _didInit$ = new ReplaySubject<true>(1);
    public reportTitle$ = merge(
        this._didInit$,
        this._router.events.pipe(filter((e) => e instanceof NavigationEnd))
    ).pipe(
        switchMap(() => this._activatedRoute.firstChild?.data ?? EMPTY),
        pluck('title')
    );

    constructor(
        public responsiveService: ResponsiveService,
        private _i18n: I18n,
        private _featureFlagService: FeatureFlagService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _insightsService: BuyerInsightsService
    ) {}

    public ngOnInit(): void {
        this._didInit$.next(true);
    }
}
